import { store } from "utils/store/configureStore";
import { signOutUser } from "utils/actions/index";
import { noteFailure, noteSuccess } from "utils/maintenance";

/**
  Service to handle ajax requests to any api
  This service is not used directly, this is

  TODO:
  - Create Put, Post and Delete

  Outage detection: every method below reports into utils/maintenance so the
  app can show a maintenance page on a genuine API outage instead of throwing
  users into a wall of broken requests. `noteSuccess()` after a good response
  and `noteFailure(status, url)` in the catch are the only two hooks needed —
  the module itself decides whether that adds up to an outage. This replaces
  the old commented-out `window.location.replace(paths.app.maintenance)` guard
  below, which fired just as often for a dropped wifi connection as for a real
  outage; see utils/maintenance.js for why.
 */

type QueryParams = Record<string, unknown>;

/** Shape resolved by every request method (and the rejection value for non-2xx responses). */
export interface ApiResponse<T = any> {
  status: number;
  json: T;
}

/** Rejection shape used by this client; may also be a native fetch error (no status). */
export interface ApiError {
  status?: number;
  message?: string;
  json?: unknown;
}

export default class RequestMethodsBase {
  /** Base URL, set by each subclass. */
  static API_URL: string | undefined;

  /**
   * Make a get request to our api
   * @param  {String} url    path to resource without host
   * @param  {Hash} params   Query params
   * @return {Promise}
   */
  static get(url: string, params?: QueryParams | null, type?: string): Promise<ApiResponse> {
    return fetch(this._mountUrlWithParams(url, params), {
      headers: this._buildHeaders(type),
      credentials: "include"
    })
      .then(this._rejectBadResponse)
      .then(this._checkAuth)
      .then(this._jsonResponse)
      .then(response => {
        noteSuccess();
        return response;
      })
      .catch((error: ApiError | undefined) => {
        noteFailure(error?.status, url);
        if (error && !error.status) {
          console.warn(`GET - requestMethodsBase - ${error?.message || error}`);
        }
        throw error;
      });
  }

  /**
   * Post request with stringified body.
   * @param  {String} url
   * @param  {Hash} data
   * @return {Promise}
   */
  static post(url: string, data?: unknown): Promise<ApiResponse> {
    return fetch(this._mountUrlWithParams(url, {}), {
      method: "POST",
      headers: this._buildHeaders(),
      credentials: "include",
      body: JSON.stringify(data)
    })
      .then(this._rejectBadResponse)
      .then(this._checkAuth)
      .then(this._jsonResponse)
      .then(response => {
        noteSuccess();
        return response;
      })
      .catch((error: ApiError | undefined) => {
        noteFailure(error?.status, url);
        throw error;
      });
  }

  static patch(url: string, data?: unknown): Promise<ApiResponse> {
    return fetch(this._mountUrlWithParams(url, {}), {
      method: "PATCH",
      headers: this._buildHeaders(),
      credentials: "include",
      body: JSON.stringify(data)
    })
      .then(this._rejectBadResponse)
      .then(this._checkAuth)
      .then(this._jsonResponse)
      .then(response => {
        noteSuccess();
        return response;
      })
      .catch((error: ApiError | undefined) => {
        noteFailure(error?.status, url);
        throw error;
      });
  }

  static put(url: string, data?: unknown): Promise<ApiResponse> {
    return fetch(this._mountUrlWithParams(url, {}), {
      method: "PUT",
      headers: this._buildHeaders(),
      credentials: "include",
      body: JSON.stringify(data)
    })
      .then(this._rejectBadResponse)
      .then(this._checkAuth)
      .then(this._jsonResponse)
      .then(response => {
        noteSuccess();
        return response;
      })
      .catch((error: ApiError | undefined) => {
        noteFailure(error?.status, url);
        throw error;
      });
  }

  /**
   * Make a delete request to our api
   * @param  {String} url    path to resource without host
   * @param  {Hash} params   Query params
   * @return {Promise}
   */
  static delete(url: string, params?: QueryParams | null): Promise<ApiResponse> {
    return fetch(this._mountUrlWithParams(url, params), {
      headers: this._buildHeaders(),
      method: "DELETE",
      credentials: "include"
    })
      .then(this._rejectBadResponse)
      .then(this._jsonResponse)
      .then(response => {
        noteSuccess();
        return response;
      })
      .catch((error: ApiError | undefined) => {
        noteFailure(error?.status, url);
        throw error;
      });
  }

  // private

  /**
   * Returns a rejected promise with a status and a message if the response
   * is invalid.
   * @param  {Response}
   * @return {Response} or new rejected promise
   */
  static _rejectBadResponse(response: Response): Response | Promise<never> {
    // Note: we might want to check for other statuses or conditions here
    if (response.status >= 500) {
      // response.body is a stream; read it as JSON so an API error message survives,
      // and fall back to the status text (never raw HTML from a proxy error page).
      return response
        .json()
        .catch(() => ({}))
        .then(json =>
          Promise.reject({
            status: response.status,
            message: response.statusText,
            json: { message: json?.message || response.statusText }
          })
        );
    } else {
      return response;
    }
  }

  /**
   * Converts the response to json
   * @param  {Response}
   * @return {Object} status, json
   */
  static _jsonResponse(response: Response): Promise<ApiResponse> {
    if (response.status === 204) {
      return Promise.resolve({
        status: response.status,
        json: {}
      });
    }

    return response
      .json()
      .catch(() => ({}))
      .then(json => {
        const body = { status: response.status, json };
        if (response.ok) {
          return body;
        } else {
          return Promise.reject(body);
        }
      });
  }

  /**
   * Check if the user is unauthorized and log them out if they are.
   * @param  {Response} response
   * @return {Response}
   */
  static _checkAuth(response: Response): Response | Promise<never> {
    if (response.status === 401) {
      store.dispatch(signOutUser());
      return Promise.reject({
        status: response.status,
        json: { message: "Your session has expired. Please log in." }
      });
    } else {
      return response;
    }
  }

  static _queryParams(source: QueryParams): string {
    let array: string[] = [];

    for (let key in source) {
      array.push(encodeURIComponent(key) + "=" + encodeURIComponent(source[key] as string));
    }

    return array.join("&");
  }

  /**
   * Simple mount url request with query params
   * @param  {String} url
   * @param  {Hash} params
   * @return {String}
   */
  static _mountUrlWithParams(url: string, params: QueryParams | null = {}): string {
    if (params && Object.keys(params).length) {
      const queryParamsSerialized = this._queryParams(params);
      // Keys and values are already encodeURIComponent-ed; encoding again turned % into %25.
      return `${this.API_URL}${url}?${queryParamsSerialized}`;
    } else {
      return `${this.API_URL}${url}`;
    }
  }

  /**
   * Build headers for fetch request.
   * Authorization is carried by the HttpOnly session cookie (see credentials: "include"
   * on each request above), not a header.
   * @return {Headers}
   */
  static _buildHeaders(type = "json"): Headers {
    const headers = new Headers();
    headers.append("Accept", "application/json");
    headers.append("content-type", `application/${type}`);
    return headers;
  }
}
